import React, { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Target,
  Filter,
  MapPin,
  Building2,
  Fuel,
  Store,
  DollarSign,
  Zap,
  Users,
  Settings,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface EnhancedPerformanceComparisonProps {
  data: any;
}

interface SurveyFilter {
  brand: string;
  geography: string;
  operation: string;
}

interface MetricComparison {
  category: string;
  metrics: {
    name: string;
    subjectValue: number;
    surveyAverage: number;
    percentile25: number;
    percentile75: number;
    topDecile: number;
    unit: string;
    isMonetary: boolean;
    trend: 'up' | 'down';
    improvementOpportunity: string;
    percentileRank: number;
  }[];
}

export default function EnhancedPerformanceComparison({ data }: EnhancedPerformanceComparisonProps) {
  const [selectedFilter, setSelectedFilter] = useState<SurveyFilter>({
    brand: 'Shell',
    geography: 'Los Angeles County',
    operation: 'High-Volume'
  });
  const [activeCategory, setActiveCategory] = useState<string>('profitability');

  // Enhanced survey comparison data with filters
  const surveyComparisons: MetricComparison[] = [
    {
      category: 'profitability',
      metrics: [
        {
          name: 'Gross Profit Margin',
          subjectValue: 28.5,
          surveyAverage: 24.2,
          percentile25: 20.8,
          percentile75: 27.6,
          topDecile: 32.1,
          unit: '%',
          isMonetary: false,
          trend: 'up',
          improvementOpportunity: 'Consider premium fuel mix optimization',
          percentileRank: 68
        },
        {
          name: 'Fuel Margin per Gallon',
          subjectValue: 0.35,
          surveyAverage: 0.28,
          percentile25: 0.22,
          percentile75: 0.33,
          topDecile: 0.42,
          unit: '$',
          isMonetary: true,
          trend: 'up',
          improvementOpportunity: 'Strong performance, monitor competitive pricing',
          percentileRank: 72
        },
        {
          name: 'Store Profit per Sq Ft',
          subjectValue: 285,
          surveyAverage: 220,
          percentile25: 180,
          percentile75: 265,
          topDecile: 350,
          unit: '$',
          isMonetary: true,
          trend: 'up',
          improvementOpportunity: 'Expand high-margin product lines',
          percentileRank: 75
        }
      ]
    },
    {
      category: 'operations',
      metrics: [
        {
          name: 'Gallons per Month',
          subjectValue: 100000,
          surveyAverage: 85000,
          percentile25: 68000,
          percentile75: 102000,
          topDecile: 125000,
          unit: 'gal',
          isMonetary: false,
          trend: 'up',
          improvementOpportunity: 'Volume growth opportunities available',
          percentileRank: 65
        },
        {
          name: 'Transactions per Day',
          subjectValue: 485,
          surveyAverage: 420,
          percentile25: 350,
          percentile75: 480,
          topDecile: 580,
          unit: '',
          isMonetary: false,
          trend: 'up',
          improvementOpportunity: 'Implement loyalty programs',
          percentileRank: 70
        },
        {
          name: 'Average Transaction Size',
          subjectValue: 42.50,
          surveyAverage: 38.20,
          percentile25: 32.80,
          percentile75: 44.10,
          topDecile: 52.30,
          unit: '$',
          isMonetary: true,
          trend: 'up',
          improvementOpportunity: 'Cross-sell opportunities in convenience store',
          percentileRank: 67
        }
      ]
    },
    {
      category: 'expenses',
      metrics: [
        {
          name: 'Labor Cost per Hour',
          subjectValue: 22.50,
          surveyAverage: 25.80,
          percentile25: 21.20,
          percentile75: 28.50,
          topDecile: 19.80,
          unit: '$',
          isMonetary: true,
          trend: 'down',
          improvementOpportunity: 'Excellent cost control',
          percentileRank: 78
        },
        {
          name: 'Utilities per Sq Ft',
          subjectValue: 9.60,
          surveyAverage: 11.40,
          percentile25: 8.90,
          percentile75: 13.20,
          topDecile: 7.50,
          unit: '$',
          isMonetary: true,
          trend: 'down',
          improvementOpportunity: 'Consider LED lighting upgrades',
          percentileRank: 72
        },
        {
          name: 'Insurance Cost per $M Coverage',
          subjectValue: 2850,
          surveyAverage: 3200,
          percentile25: 2650,
          percentile75: 3650,
          topDecile: 2200,
          unit: '$',
          isMonetary: true,
          trend: 'down',
          improvementOpportunity: 'Safety program benefits showing',
          percentileRank: 68
        }
      ]
    }
  ];

  const filterOptions = {
    brand: ['Shell', 'Chevron', 'Mobil', 'BP', 'Independent'],
    geography: ['Los Angeles County', 'Southern California', 'Central California', 'Northern California'],
    operation: ['High-Volume', 'Medium-Volume', 'Low-Volume', 'Highway', 'Urban', 'Rural']
  };

  const categoryIcons = {
    profitability: DollarSign,
    operations: Fuel,
    expenses: Settings
  };

  const categoryLabels = {
    profitability: 'Profitability',
    operations: 'Operations',
    expenses: 'Expenses'
  };

  const getPerformanceColor = (percentileRank: number) => {
    if (percentileRank >= 75) return 'text-green-600';
    if (percentileRank >= 50) return 'text-blue-600';
    if (percentileRank >= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBg = (percentileRank: number) => {
    if (percentileRank >= 75) return 'bg-green-50 border-green-200';
    if (percentileRank >= 50) return 'bg-blue-50 border-blue-200';
    if (percentileRank >= 25) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getPerformanceIcon = (percentileRank: number) => {
    if (percentileRank >= 75) return CheckCircle;
    if (percentileRank >= 50) return Target;
    if (percentileRank >= 25) return Info;
    return AlertTriangle;
  };

  const formatValue = (value: number, unit: string, isMonetary: boolean) => {
    if (isMonetary && unit === '$') {
      return `$${value.toLocaleString()}`;
    } else if (unit === '%') {
      return `${value.toFixed(1)}%`;
    } else if (unit === 'gal') {
      return `${value.toLocaleString()} gal`;
    } else if (unit === '') {
      return value.toLocaleString();
    }
    return `${value.toLocaleString()} ${unit}`;
  };

  const currentComparison = surveyComparisons.find(c => c.category === activeCategory);

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Performance Benchmarking Analysis
            </h2>
            <p className="text-gray-600">
              Compare your property against survey standards filtered by brand, geography, and operation type
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Survey Sample Size</div>
            <div className="text-2xl font-bold text-blue-600">500+</div>
            <div className="text-sm text-gray-500">Properties</div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Brand Filter
            </label>
            <select
              value={selectedFilter.brand}
              onChange={(e) => setSelectedFilter({...selectedFilter, brand: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filterOptions.brand.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Geography Filter
            </label>
            <select
              value={selectedFilter.geography}
              onChange={(e) => setSelectedFilter({...selectedFilter, geography: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filterOptions.geography.map(geo => (
                <option key={geo} value={geo}>{geo}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Store className="w-4 h-4 inline mr-1" />
              Operation Type
            </label>
            <select
              value={selectedFilter.operation}
              onChange={(e) => setSelectedFilter({...selectedFilter, operation: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filterOptions.operation.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 p-1 rounded-lg">
          {Object.entries(categoryLabels).map(([key, label]) => {
            const Icon = categoryIcons[key as keyof typeof categoryIcons];
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-6 py-3 rounded-md font-medium transition-colors flex items-center gap-2 ${
                  activeCategory === key
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Performance Metrics Grid */}
      {currentComparison && (
        <div className="space-y-4">
          {currentComparison.metrics.map((metric, index) => {
            const Icon = getPerformanceIcon(metric.percentileRank);

            return (
              <Card key={index} className={`p-6 ${getPerformanceBg(metric.percentileRank)}`}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Metric Header */}
                  <div className="lg:col-span-3">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${getPerformanceColor(metric.percentileRank)}`} />
                        <h3 className="text-xl font-semibold text-gray-900">{metric.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPerformanceColor(metric.percentileRank)} bg-opacity-20`}>
                          {metric.percentileRank}th Percentile
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatValue(metric.subjectValue, metric.unit, metric.isMonetary)}
                        </div>
                        <div className="text-sm text-gray-500">Your Value</div>
                      </div>
                    </div>
                  </div>

                  {/* Visual Comparison Bar */}
                  <div className="lg:col-span-2">
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Survey Distribution ({selectedFilter.brand} • {selectedFilter.geography} • {selectedFilter.operation})</span>
                      </div>

                      {/* Performance Range Visualization */}
                      <div className="relative">
                        <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                          {/* Quartile ranges */}
                          <div
                            className="absolute h-full bg-red-200"
                            style={{left: '0%', width: '25%'}}
                          />
                          <div
                            className="absolute h-full bg-yellow-200"
                            style={{left: '25%', width: '25%'}}
                          />
                          <div
                            className="absolute h-full bg-blue-200"
                            style={{left: '50%', width: '25%'}}
                          />
                          <div
                            className="absolute h-full bg-green-200"
                            style={{left: '75%', width: '25%'}}
                          />

                          {/* Subject property position */}
                          <div
                            className="absolute top-0 w-1 h-full bg-purple-600 shadow-lg"
                            style={{left: `${metric.percentileRank}%`}}
                          />
                        </div>

                        {/* Range labels */}
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>Min</span>
                          <span className="text-red-600">25th</span>
                          <span className="text-yellow-600">50th</span>
                          <span className="text-blue-600">75th</span>
                          <span className="text-green-600">Top 10%</span>
                        </div>

                        {/* Value markers */}
                        <div className="flex justify-between text-xs font-medium mt-1">
                          <span>{formatValue(metric.percentile25 * 0.8, metric.unit, metric.isMonetary)}</span>
                          <span>{formatValue(metric.percentile25, metric.unit, metric.isMonetary)}</span>
                          <span>{formatValue(metric.surveyAverage, metric.unit, metric.isMonetary)}</span>
                          <span>{formatValue(metric.percentile75, metric.unit, metric.isMonetary)}</span>
                          <span>{formatValue(metric.topDecile, metric.unit, metric.isMonetary)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Improvement Insights */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Improvement Insights</h4>
                    <p className="text-sm text-gray-600 mb-3">{metric.improvementOpportunity}</p>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Survey Average:</span>
                        <span className="font-medium">
                          {formatValue(metric.surveyAverage, metric.unit, metric.isMonetary)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Top Decile:</span>
                        <span className="font-medium text-green-600">
                          {formatValue(metric.topDecile, metric.unit, metric.isMonetary)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Gap to Top 10%:</span>
                        <span className="font-medium">
                          {formatValue(Math.abs(metric.topDecile - metric.subjectValue), metric.unit, metric.isMonetary)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Survey Methodology */}
      <Card className="p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Survey Methodology</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div>
            <strong>Data Source:</strong> Annual Fuel Central Industry Survey
          </div>
          <div>
            <strong>Sample Period:</strong> Rolling 12-month data
          </div>
          <div>
            <strong>Update Frequency:</strong> Monthly with quarterly validation
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          *Benchmarks are filtered based on your selected criteria and represent {selectedFilter.brand} stations in {selectedFilter.geography} regions with {selectedFilter.operation} operations.
        </p>
      </Card>
    </div>
  );
}